import i18n from 'i18next';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import {TCompareOption, TProps, TSelectOption, TValue, TWritablePropKeys} from './types';

export const DEFAULT_VISIBLE_ELEMENT_COUNT = 6;

export const getValue = (value?: TValue, multiple?: boolean): TValue => {
  if (multiple) {
    return Array.isArray(value) ? value : [];
  }

  return value ?? null;
};

const addParentValueAndLabelToChild = (option: TSelectOption): TSelectOption => {
  if (!option.children) {
    return option;
  }

  return {
    ...option,
    children: option.children.map((child) => ({
      ...addParentValueAndLabelToChild(child),
      parentValue: option.value,
      parentLabel: child.parentLabel ?? option.label,
    })),
  };
};

export const getOptions = (
  options: TSelectOption[],
  {
    isDisplayEmpty,
    emptyLabel,
    multiple,
  }: {
    isDisplayEmpty: TProps['isDisplayEmpty'];
    emptyLabel: TProps['emptyLabel'];
    multiple: TProps['multiple'];
  },
) => {
  const preparedOptions = options.map(addParentValueAndLabelToChild);

  if (isDisplayEmpty && !multiple) {
    return [
      {
        value: null,
        label: emptyLabel ?? i18n.t(['EdnaSelect:notSelected', 'Select:notSelected']),
      },
      ...preparedOptions,
    ];
  }

  return preparedOptions;
};

export const getSelectedOptions = (
  value: TValue,
  compareOption: TCompareOption,
  options: TSelectOption[],
): TSelectOption[] => {
  const findInOptions = (targetOptions: TSelectOption[]): TSelectOption[] =>
    targetOptions.reduce((result, option) => {
      if (Array.isArray(value) && value.some((item) => compareOption(option, item))) {
        return [...result, option];
      }

      if (!Array.isArray(value) && compareOption(option, value)) {
        return [...result, option];
      }

      if (option.children) {
        return [...result, ...findInOptions(option.children)];
      }

      return result;
    }, [] as TSelectOption[]);

  const selectedOptions = findInOptions(options);

  if (value === '') {
    const emptyOption = options.find(
      (option) => option.value === null || option.value === undefined,
    );

    if (emptyOption) {
      selectedOptions.push(emptyOption);
    }
  }

  return selectedOptions;
};

export const getValuesFromOptions = (options: TSelectOption[]): TValue[] =>
  options.map(({value}) => value);

const matchTypes = new Set(['string', 'number']);

const getValueForSearch = (value: TValue) =>
  matchTypes.has(typeof value) ? String(value).toLowerCase() : '';

export const filterOptionBySearch = (
  option: TSelectOption,
  lowerSearch: string,
  isValueCompareInSearch?: boolean,
) => {
  const value = getValueForSearch(option.value);
  const label = getValueForSearch(option.label);

  return (isValueCompareInSearch && value.includes(lowerSearch)) || label.includes(lowerSearch);
};

export const debouncedCall = debounce((callback) => callback(), 500);

export const defaultCompareOption: TCompareOption = (option, value) => isEqual(option.value, value);

export const writableProps: Required<Pick<TProps, TWritablePropKeys>> = {
  options: [],
  compareOption: defaultCompareOption,
  emptyLabel: null,
  isDisplayEmpty: false,
  isValueCompareInSearch: false,
};

// TODO удалить, воспользоваться provider или оберткой, чтобы менять пропсы
export const setWritableProps = (props: Partial<Pick<TProps, TWritablePropKeys>>) => {
  Object.assign(writableProps, props);
};
